import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Field, reduxForm} from 'redux-form';
import _pick from 'lodash/pick';
import S from 'StyledProlongSession.js';
import { requestToSendSmsCodeToProlongSession, verifySmsCodeToProlongSession, logOutUser } from 'authActions.js';
import { translation } from 'helpers/utilsHelper.js';

import TimerIcon from 'timer_icon.svg';

// Validation
import validation from 'sb-validate-js/validate.js';
import validators from 'validators.enum.js';

const validate = values => {
    const errors = validation(values, validators.SMS_CODE_TO_PROLONG_SESSION);
    return errors;
};

const SEND_SMS_CODE = 'SEND_SMS_CODE';
const VERIFY_SMS_CODE = 'VERIFY_SMS_CODE';
const PROLONG_SESSION_CONFIRMATION = 'PROLONG_SESSION_CONFIRMATION';
const SESSION_CLOSED_CONFIRMATION = 'SESSION_CLOSED_CONFIRMATION';

const maxSmsRequests = 3;

const ProlongSession = ({toggleProlongSessionModal, showSessionClosedInfo, requestToSendSmsCodeToProlongSession, verifySmsCodeToProlongSession, logOutUser, user,  handleSubmit, pristine, submitting,
    smsRequestSessionExtensionCounter, errorSendSms}) => {

    const [step, setStep] = useState(showSessionClosedInfo ? SESSION_CLOSED_CONFIRMATION : SEND_SMS_CODE);
    const [errorMessage, setErrorMessage] = useState(null);

    const endSesion = () => {
        setStep(SESSION_CLOSED_CONFIRMATION);
    };

    const logout = () => {
        logOutUser(true);
    }

    const onFormSubmit = async(values) => {
        try {
            if (user?.prolongSession?.smsCodeAttemptsLeft == 0){
                endSesion();
                return;
            }

            let reloginData = _pick(values,['verificationCode']);
            reloginData.mobile = user?.mobileNumber;
            const response = await verifySmsCodeToProlongSession(reloginData);
            if (response == true){
                setStep(PROLONG_SESSION_CONFIRMATION);
            } else if (response == 'verificationCode_error') {
                setErrorMessage('prolongSession_wrongSmsCode');
            } else if (response == 'verificationCode_wrongSmsCode_exceeded'){
                endSesion();
            }
        } catch(ex){
            console.log(ex);
        }
    }

    return (
        <S.SendSmsCodeForm method="post" onSubmit={handleSubmit(onFormSubmit)}>
            <S.MainContainer>
                <S.ProlongSessionTopRow>
                    <S.TopRowLeftSide>
                        <S.SessionDeactive> {translation('prolongSession_sessionDeactive')} &nbsp;-&nbsp; </S.SessionDeactive>
                        <S.ProlongSessionUserDetails> {translation('prolongSession_mobileUser')} </S.ProlongSessionUserDetails>
                        <S.ProlongSessionUserMobile> &nbsp; {user?.mobileNumber} </S.ProlongSessionUserMobile>
                    </S.TopRowLeftSide>
                    <S.TopRowRightSide>
                        <S.BalanceLabel> {translation('sessionExtension_balance')} </S.BalanceLabel>
                        <S.BalanceBackground> {user?.balance?.toFixed(2)} </S.BalanceBackground>
                    </S.TopRowRightSide>
                </S.ProlongSessionTopRow>
            
                {step == SEND_SMS_CODE && <SendSmsCodeStep endSesion={endSesion} setStep={setStep} requestToSendSmsCodeToProlongSession={requestToSendSmsCodeToProlongSession} smsRequestSessionExtensionCounter={smsRequestSessionExtensionCounter} user={user}></SendSmsCodeStep>}
                {step == VERIFY_SMS_CODE && <VerifySmsCodeStep endSesion={endSesion} pristine={pristine} submitting={submitting} requestToSendSmsCodeToProlongSession={requestToSendSmsCodeToProlongSession} errorMessage={errorMessage} smsRequestSessionExtensionCounter={smsRequestSessionExtensionCounter} setStep={setStep} errorSendSms={errorSendSms} user={user}></VerifySmsCodeStep>}           
                {step == PROLONG_SESSION_CONFIRMATION && <ProlongSessionConfirmationStep closeModal={toggleProlongSessionModal}></ProlongSessionConfirmationStep>}
                {step == SESSION_CLOSED_CONFIRMATION && <SessionClosedConfirmationStep closeModal={logout}></SessionClosedConfirmationStep>}
            </S.MainContainer>
        </S.SendSmsCodeForm>
    );
}


const SendSmsCodeStep = ({endSesion, setStep, requestToSendSmsCodeToProlongSession, smsRequestSessionExtensionCounter, user}) => {

    const continueSession = () => {
        if (user?.prolongSession?.sendSMSAttemptsLeft == 0){
            setStep(SESSION_CLOSED_CONFIRMATION);
            return;
        }
        if (smsRequestSessionExtensionCounter < maxSmsRequests){
            requestToSendSmsCodeToProlongSession();
            setStep(VERIFY_SMS_CODE);
        } else {
            setStep(SESSION_CLOSED_CONFIRMATION);
        }
    };

    return (
        <S.ProlongSessionWrapper>
            <S.TimerIcon dangerouslySetInnerHTML={{ __html: TimerIcon }}></S.TimerIcon>
            <S.ProlongSessionHighlightedMessage> {translation('prolongSession_timePassed')} </S.ProlongSessionHighlightedMessage>
            <S.ProlongSessionHighlightedMessage> {translation('prolongSession_continuePlay')} </S.ProlongSessionHighlightedMessage>
            <S.ProlongSessionMainMessage> {translation('prolongSession_mainMessage')} </S.ProlongSessionMainMessage>

            <S.ButtonsTopBorder></S.ButtonsTopBorder>
            <S.ButtonsContainer>
                <S.ButtonAction className="continue" onClick={() => continueSession()}> {translation('prolongSession_continue')} </S.ButtonAction>
                <S.ButtonAction className="leave" onClick={() => endSesion()}> {translation('prolongSession_leave')} </S.ButtonAction>
            </S.ButtonsContainer>
        </S.ProlongSessionWrapper>
    )
};


const renderField = ({
    input,
    type,
    meta: {touched, error, warning}
}) => {
    return (
        <S.InputWrapper>
            <S.SmsCodeInput {...input} type={type}/>
            {touched && ((error && <S.ErrorMessage>{error[0]}</S.ErrorMessage>) || (warning && <S.ErrorMessage>{warning}</S.ErrorMessage>))}
        </S.InputWrapper>     
    )
};


const VerifySmsCodeStep = ({endSesion, requestToSendSmsCodeToProlongSession, errorMessage=null, pristine, submitting, smsRequestSessionExtensionCounter, setStep, errorSendSms, user}) => {

    const coolDownToResend = 10;
    const [resendSmsCodeButtonDisabled, setResendSmsCodeButtonDisabled] = useState(true);
    const [counter, setCounter] = useState(-1);

    useEffect(() => {
        if (user?.prolongSession?.sendSMSAttemptsLeft == 0){
            setStep(SESSION_CLOSED_CONFIRMATION);
        }
        if (errorSendSms){
            setCounter(coolDownToResend);
        }
    }, [errorSendSms]); 

    useEffect(() => {
        if (counter < 0){
            return;
        }
        const interval = setInterval(() => {
            if (counter >= 0){
                setCounter(counter-1);
            } 
            if (counter == 0){
                setResendSmsCodeButtonDisabled(false);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [counter]);

    const resendSmsCode = () => {
        
        if (user?.prolongSession?.sendSMSAttemptsLeft == 0){
            setStep(SESSION_CLOSED_CONFIRMATION);
            return;
        }

        if (smsRequestSessionExtensionCounter < maxSmsRequests){
            requestToSendSmsCodeToProlongSession();
            setResendSmsCodeButtonDisabled(true);
        } else {
            setStep(SESSION_CLOSED_CONFIRMATION);
        }
    }

    return (
        <S.ProlongSessionWrapper className="padding-top">
             <S.ProlongSessionHighlightedMessage> {translation('prolongSession_enterSmsCode')} </S.ProlongSessionHighlightedMessage>
             <S.ProlongSessionPutSmsCodeMessage> {translation('prolongSession_shouldReceiveSmsCode')} </S.ProlongSessionPutSmsCodeMessage>
             <S.InputContainer>
                <Field
                    name="verificationCode"
                    type="text"
                    component={renderField}
                />
                <S.ErrorMessage> {translation(errorMessage)} </S.ErrorMessage>
                <S.SendAgainContainer>
                    <S.Counter className={counter == -1 ? `visibility-hidden`:``}>{counter}</S.Counter>
                    <S.ResendSmsCode disabled={resendSmsCodeButtonDisabled} onClick={() => resendSmsCode()}> {translation('prolongSession_sendAgain')} </S.ResendSmsCode>
                </S.SendAgainContainer>
             </S.InputContainer>

            <S.ButtonsTopBorder></S.ButtonsTopBorder>
            <S.ButtonsContainer>
                <S.ButtonAction className="leave" onClick={() => endSesion()}> {translation('prolongSession_leave')} </S.ButtonAction>
                <S.ButtonAction type="submit" disabled={pristine || submitting} className="reLogin"> {translation('prolongSession_reLogin')} </S.ButtonAction>
            </S.ButtonsContainer>
        </S.ProlongSessionWrapper>
    );
};


const ProlongSessionConfirmationStep = ({closeModal}) => {

    return (<S.ProlongSessionWrapper className="padding-top">
        <S.ProlongSessionHighlightedMessage> {translation('prolongSession_sessionExtendedMessage1')}  </S.ProlongSessionHighlightedMessage>
        <S.ProlongSessionMainMessage> {translation('prolongSession_sessionExtendedMessage2')} </S.ProlongSessionMainMessage>
        <S.ButtonsTopBorder></S.ButtonsTopBorder>
        <S.ButtonsContainer>
            <S.ButtonAction className="confirm" onClick={() => closeModal()}> {translation('prolongSession_close')} </S.ButtonAction>
        </S.ButtonsContainer>
    </S.ProlongSessionWrapper>)
}

const SessionClosedConfirmationStep = ({closeModal}) => {

    return (<S.ProlongSessionWrapper className="padding-top">
        <S.ProlongSessionHighlightedMessage> {translation('prolongSession_sessionClosedMessage1')}  </S.ProlongSessionHighlightedMessage>
        <S.ProlongSessionMainMessage> {translation('prolongSession_sessionClosedMessage2')} </S.ProlongSessionMainMessage>
        <S.ButtonsTopBorder></S.ButtonsTopBorder>
        <S.ButtonsContainer>
            <S.ButtonAction className="confirm" onClick={() => closeModal()}> {translation('prolongSession_close')} </S.ButtonAction>
        </S.ButtonsContainer>
    </S.ProlongSessionWrapper>)
}

const mapStateToProps = (state) => {
    const {Auth: {user, error, smsRequestSessionExtensionCounter, isOpenProlongSessionModalObject}} = state;
    return {
        user,
        errorSendSms: error,
        smsRequestSessionExtensionCounter,
        showSessionClosedInfo: isOpenProlongSessionModalObject?.sessionClosed
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        requestToSendSmsCodeToProlongSession: bindActionCreators(requestToSendSmsCodeToProlongSession, dispatch),
        verifySmsCodeToProlongSession: bindActionCreators(verifySmsCodeToProlongSession, dispatch),
        logOutUser: bindActionCreators(logOutUser, dispatch),
        dispatch
    }
};

let ProlongSessionReduxForm = reduxForm({
    form: 'sendSmsCodeToProlongSessionForm',
    validate,
    enableReinitialize: true
})(ProlongSession)


ProlongSessionReduxForm = connect(mapStateToProps, mapDispatchToProps)(ProlongSessionReduxForm);

export default ProlongSessionReduxForm 


